// trackcoprv2/frontend/src/components/CRSSuggestions.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance'; // Use the configured instance
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb'; // Suggestion icon
import { useAuth } from '../context/AuthContext'; // <-- Import AuthContext

// --- Constants ---
const API_BASE_URL = process.env.REACT_APP_API_BASE || ''; // Use environment variable or default
const SUGGESTIONS_ENDPOINT = `${API_BASE_URL}/api/crs-suggestions`;
const STYLING_CONFIG = {
    paper: {
        mt: { xs: 2, md: 0 },
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        // height: '100%', // <-- REMOVE OR COMMENT OUT THIS LINE
        display: 'flex', // Keep flex settings if needed for internal layout
        flexDirection: 'column',
    },
    title: {
        textAlign: 'center',
        mb: 2, // Margin bottom for title
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 1, // Allow loading/error/message to take available space
        minHeight: '150px', // Ensure minimum height for loading/error/message
        textAlign: 'center', // Center text messages
        p: 2, // Add padding for messages within the box
    },
    suggestionList: {
        mt: 1, // Margin top for the list
        overflowY: 'auto', // Allow scrolling if suggestions are long
        flexGrow: 1,
        // Ensure list doesn't shrink the container excessively
        minHeight: '100px',
    },
    listItem: {
        alignItems: 'flex-start', // Align icon with the start of the text
        py: 0.5, // Adjust vertical padding
    },
    listItemIcon: {
        minWidth: 'auto', // Adjust icon spacing
        marginRight: 1.5,
        marginTop: '4px', // Align icon slightly lower
        color: 'primary.main', // Use theme color
    },
};

// --- Helper Function ---
// Parses the suggestion string/array/object into a list of items.
const parseSuggestions = (suggestionsData) => {
    if (!suggestionsData) {
        return [];
    }
    // Check if suggestionsData is an array (direct array from JSON response)
    if (Array.isArray(suggestionsData)) {
        return suggestionsData.filter(suggestion => typeof suggestion === 'string' && suggestion.trim() !== '');
    }
    // Check if suggestionsData is an object with a suggestions array
    if (typeof suggestionsData === 'object' && suggestionsData !== null && Array.isArray(suggestionsData.suggestions)) {
        return suggestionsData.suggestions.filter(suggestion => typeof suggestion === 'string' && suggestion.trim() !== '');
    }
    // Fallback to original string parsing logic (for bulleted lists)
    if (typeof suggestionsData === 'string') {
        return suggestionsData
            .split('\n')
            .map(line => line.trim())
            // Allow lines starting with '*' or '-' optionally followed by a space
            .filter(line => /^[*|-]\s*/.test(line))
             // Remove the bullet point/dash and trim
            .map(line => line.replace(/^[*|-]\s*/, '').trim())
            .filter(line => line.length > 0); // Ensure non-empty lines after processing
    }
    console.warn("Suggestions data received in unexpected format:", suggestionsData);
    return [];
};


// --- CRSSuggestions Component ---
function CRSSuggestions({ crsResult }) {
    const { user, loading: authLoading } = useAuth(); // Get user and auth loading state
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionLimitReached, setSessionLimitReached] = useState(false); // State for free tier limit

    // --- Effect Hook ---
    useEffect(() => {
        // 1. Wait for auth state to load
        if (authLoading) {
            setIsLoading(false); // Ensure loading spinner for suggestions isn't shown
            setError(null);
            setSuggestions([]);
            setSessionLimitReached(false);
            return;
        }

        // 2. Check if crsResult is valid (required for any action or display)
        if (!crsResult || typeof crsResult !== 'object' || Object.keys(crsResult).length === 0) {
            setSuggestions([]); // Clear suggestions if result is cleared or invalid
            setError(null);
            setIsLoading(false);
             // Don't reset sessionLimitReached here if user is logged in, as it persists
            if (!user) setSessionLimitReached(false); // Reset if logged out and result clears
            return;
        }

        // 3. If logged out, handle via renderContent. Clear any stale logged-in state.
        if (!user) {
            setSuggestions([]);
            setError(null);
            setIsLoading(false);
            setSessionLimitReached(false); // Ensure limit message is cleared
            return;
        }

        // --- Logged-in User Logic ---

        // 4. Check free tier session limit BEFORE attempting fetch
        const hasFetchedKey = `crsSuggestionFetched_${user.id}_free`; // User-specific key
        const hasFetchedThisSession = sessionStorage.getItem(hasFetchedKey);

        if (user.membershipTier === 'free' && hasFetchedThisSession) {
            setSessionLimitReached(true);
            setSuggestions([]);
            setError(null);
            setIsLoading(false);
            return; // Stop fetching
        } else {
            // Reset visual state if not free or haven't fetched yet.
            // Note: This reset happens *before* the fetch attempt below.
            setSessionLimitReached(false);
        }


        // 5. Fetch suggestions for logged-in user (if not limit reached)
        const fetchSuggestions = async () => {
            // Double check conditions before fetching - defensive programming
             if (!user || sessionLimitReached || !crsResult || typeof crsResult !== 'object' || Object.keys(crsResult).length === 0) {
                 console.log("Skipping fetch: Conditions not met.", { user: !!user, sessionLimitReached, hasCrsResult: !!crsResult});
                 return;
             }

            setIsLoading(true);
            setError(null);
            setSuggestions([]); // Clear previous before new fetch

            try {
                const response = await axiosInstance.post(SUGGESTIONS_ENDPOINT, {
                    crsResult,
                    userId: user.id // Send userId if backend uses it
                });
                const parsed = parseSuggestions(response.data);

                if (parsed.length > 0) {
                    setSuggestions(parsed);
                    // Set session flag for free users AFTER successful fetch
                    if (user.membershipTier === 'free') {
                        sessionStorage.setItem(hasFetchedKey, 'true');
                    }
                } else {
                    // Handle valid response but no suggestions / parsing failure
                    // Show a user-friendly message instead of empty list
                     setError('No suggestions could be generated at this time, or the format was unexpected.');
                    console.warn("Received suggestions response but no valid suggestions found/parsed:", response.data);
                }
            } catch (err) {
                console.error('Error fetching CRS suggestions:', err);
                // Check for the specific 503 Service Unavailable error
                if (err.response?.status === 503 && err.response?.data?.error === 'SERVICE_UNAVAILABLE') {
                    setError("The website maker is too broke to pay for credits. Please check back next month.");
                } else {
                    const errorMsg = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to load suggestions.';
                    setError(errorMsg);
                }
                 setSuggestions([]); // Ensure suggestions are cleared on error
            } finally {
                setIsLoading(false);
            }
        };

        // Call fetchSuggestions ONLY if not already loading and conditions are met
        // The checks inside fetchSuggestions provide an additional layer of safety.
        if (!isLoading) {
             fetchSuggestions();
        }
        // No cleanup function needed currently as axios handles cancellation poorly without AbortController
        // If requests could be triggered very rapidly, consider implementing AbortController

    }, [crsResult, user, authLoading, isLoading, sessionLimitReached]);


    // --- Conditional Rendering Logic ---
    const renderContent = () => {
        // Order matters: Check most specific/blocking states first

        // Auth still loading? (Should be handled by outer check, but safe)
        if (authLoading) { // This case is less likely due to the outer return null, but kept for robustness
            return (
                <Box sx={STYLING_CONFIG.loadingBox}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Checking authentication...</Typography>
                </Box>
            );
        }

        // Not logged in?
        if (!user) {
            return (
                <Box sx={STYLING_CONFIG.loadingBox}>
                    <Typography variant="body1">Log in to view personalized suggestions to improve your score.</Typography>
                </Box>
            );
        }

        // Logged in: Check session limit for free tier
        if (sessionLimitReached) {
            return (
                <Box sx={STYLING_CONFIG.loadingBox}>
                    <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
                        Free tier suggestion limit reached for this browser session. Upgrade your plan for unlimited suggestions, or try again in a new session.
                    </Alert>
                </Box>
            );
        }

        // Logged in, not limited: Check loading state for suggestions
        if (isLoading) {
            return (
                <Box sx={STYLING_CONFIG.loadingBox}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading suggestions...</Typography>
                </Box>
            );
        }

        // Logged in, not limited, not loading: Check for errors
        if (error) {
            return (
                <Box sx={STYLING_CONFIG.loadingBox}>
                    <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{`Error: ${error}`}</Alert>
                </Box>
            );
        }

        // Logged in, not limited, not loading, no errors: Display suggestions if available
        if (suggestions.length > 0) {
            return (
                <List sx={STYLING_CONFIG.suggestionList} dense>
                    {suggestions.map((suggestion, index) => (
                        <ListItem key={index} sx={STYLING_CONFIG.listItem}>
                            <ListItemIcon sx={STYLING_CONFIG.listItemIcon}>
                                <LightbulbIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={suggestion} />
                        </ListItem>
                    ))}
                </List>
            );
        }

        // Logged in, not limited, not loading, no errors, no suggestions yet (e.g., initial state before fetch completes, or API returned empty but valid)
        // This might briefly show if the fetch is very fast.
        return (
            <Box sx={STYLING_CONFIG.loadingBox}>
                <Typography variant="body1">Improvement suggestions will appear here.</Typography>
            </Box>
        );
    };

    // --- Main Component Render ---

    // Render the main component structure ONLY if:
    // 1. Auth is not loading AND
    // 2. crsResult is present and not empty
    // This prevents the suggestion box from appearing before there's a score to base suggestions on.
    if (authLoading || !crsResult || typeof crsResult !== 'object' || Object.keys(crsResult).length === 0) {
        // If we are not rendering the Paper, we don't need to render anything here.
        // The parent component's layout handles the space.
        return null;
    }

    // If conditions met, render the Paper container with dynamic content.
    return (
        <Paper sx={STYLING_CONFIG.paper} elevation={2}>
            <Typography variant="h6" component="p" sx={STYLING_CONFIG.title}>
                Improvement Suggestions
            </Typography>
            {renderContent()}
        </Paper>
    );
}

export default CRSSuggestions;